import { z } from "zod";
import { CAPABILITIES } from "./capabilities.js";
/**
 * AGENT ↔ SUNUCU WEBSOCKET PROTOKOLÜ
 *
 * Bağlantıyı her zaman agent açar (giden WSS). PC'de açık port yoktur; NAT/dinamik IP
 * sorunu bu sayede yoktur. Sunucu bağlantı üstünden iş teklif eder.
 */
export const PROTOCOL_VERSION = 1;
export const jobStatus = z.enum([
    "queued",
    "claimed",
    "running",
    "succeeded",
    "failed",
    "canceled",
    "expired",
]);
export const processStatus = z.enum(["starting", "ready", "stopping", "stopped", "crashed"]);
// --- Agent → Sunucu -------------------------------------------------------
export const helloMsg = z.object({
    t: z.literal("hello"),
    protocol: z.literal(PROTOCOL_VERSION),
    agentName: z.string().min(1),
    agentKind: z.enum(["pc", "vps"]),
    agentVersion: z.string(),
    capabilities: z.array(z.enum(CAPABILITIES)),
    /** Agent'ın erişebildiği çalışma alanları — sunucu job'ı buna göre yönlendirir. */
    workspaces: z.record(z.string(), z.string()),
    os: z.string(),
});
export const heartbeatMsg = z.object({
    t: z.literal("heartbeat"),
    runningJobs: z.array(z.string().uuid()),
    load: z.object({ cpu: z.number(), memFreeMb: z.number() }).optional(),
});
export const jobClaimMsg = z.object({
    t: z.literal("job.claim"),
    jobId: z.string().uuid(),
});
export const jobLogMsg = z.object({
    t: z.literal("job.log"),
    jobId: z.string().uuid(),
    seq: z.number().int().nonnegative(),
    stream: z.enum(["stdout", "stderr", "system"]),
    /** ANSI kaçış dizileri KORUNUR — istemcide xterm.js render eder. */
    chunk: z.string(),
});
export const jobProgressMsg = z.object({
    t: z.literal("job.progress"),
    jobId: z.string().uuid(),
    step: z.string(),
    pct: z.number().min(0).max(100).optional(),
    note: z.string().optional(),
});
export const jobResultMsg = z.object({
    t: z.literal("job.result"),
    jobId: z.string().uuid(),
    status: z.enum(["succeeded", "failed", "canceled"]),
    error: z.string().optional(),
    result: z.unknown().optional(),
    artifacts: z
        .array(z.object({
        kind: z.enum(["aab", "apk", "web-export", "log", "report"]),
        path: z.string(),
        sha256: z.string().optional(),
        size: z.number().optional(),
    }))
        .optional(),
});
export const processReadyMsg = z.object({
    t: z.literal("process.ready"),
    jobId: z.string().uuid(),
    /** Agent stdout'tan ayrıştırır: http://localhost:8081 */
    url: z.string().optional(),
    port: z.number().optional(),
    pid: z.number().optional(),
});
export const processExitMsg = z.object({
    t: z.literal("process.exit"),
    jobId: z.string().uuid(),
    status: processStatus,
    exitCode: z.number().nullable(),
});
export const agentToServer = z.discriminatedUnion("t", [
    helloMsg,
    heartbeatMsg,
    jobClaimMsg,
    jobLogMsg,
    jobProgressMsg,
    jobResultMsg,
    processReadyMsg,
    processExitMsg,
]);
// --- Sunucu → Agent -------------------------------------------------------
export const welcomeMsg = z.object({
    t: z.literal("welcome"),
    agentId: z.string().uuid(),
    serverTime: z.string(),
    /** Sunucunun kabul ettiği yetenekler (agent'ın bildirdiğiyle kesişim). */
    grantedCapabilities: z.array(z.enum(CAPABILITIES)),
});
export const jobOfferMsg = z.object({
    t: z.literal("job.offer"),
    jobId: z.string().uuid(),
    type: z.string(),
    app: z.string().optional(),
    params: z.unknown(),
    /** Bu süreye kadar claim edilmezse job expired olur. */
    expiresAt: z.string(),
});
export const jobCancelMsg = z.object({
    t: z.literal("job.cancel"),
    jobId: z.string().uuid(),
    reason: z.string().optional(),
});
export const pingMsg = z.object({ t: z.literal("ping") });
export const serverToAgent = z.discriminatedUnion("t", [
    welcomeMsg,
    jobOfferMsg,
    jobCancelMsg,
    pingMsg,
]);
// --- Sunucu → İstemci (konsol) --------------------------------------------
export const clientEvent = z.discriminatedUnion("t", [
    z.object({
        t: z.literal("agent.status"),
        agentId: z.string().uuid(),
        name: z.string(),
        kind: z.enum(["pc", "vps"]),
        online: z.boolean(),
        lastSeen: z.string(),
    }),
    z.object({
        t: z.literal("job.update"),
        jobId: z.string().uuid(),
        status: jobStatus,
        step: z.string().optional(),
        pct: z.number().optional(),
    }),
    z.object({
        t: z.literal("job.log"),
        jobId: z.string().uuid(),
        seq: z.number(),
        stream: z.enum(["stdout", "stderr", "system"]),
        chunk: z.string(),
    }),
    z.object({
        t: z.literal("process.status"),
        jobId: z.string().uuid(),
        status: processStatus,
        url: z.string().optional(),
    }),
    z.object({
        t: z.literal("alert"),
        level: z.enum(["info", "warn", "error"]),
        title: z.string(),
        body: z.string(),
        source: z.string(),
    }),
]);
// --- Zamanlayıcı sabitleri ------------------------------------------------
export const TIMINGS = {
    /** Agent bu aralıkla heartbeat yollar. */
    heartbeatMs: 15_000,
    /** Bu süre heartbeat gelmezse agent çevrimdışı sayılır. */
    agentOfflineMs: 45_000,
    /** running job'da bu süre sessizlik → failed. */
    jobStaleMs: 10 * 60_000,
    /** queued job bu süre içinde claim edilmezse expired. */
    jobClaimTtlMs: 24 * 60 * 60_000,
};
//# sourceMappingURL=protocol.js.map